import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "../lib/api";
import { ClickSplit } from "../components/click-split";

interface Campaign {
  id: string; name: string; status: string;
  totalRecipients: number; sentCount: number; deliveredCount: number;
  hardBounceCount: number; openedCount: number; clickedCount: number;
  unsubscribedCount: number; complainedCount: number;
  createdAt: string;
}

export default function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => api<{ campaigns: Campaign[] }>("/api/campaigns"),
  });

  const campaigns = data?.campaigns ?? [];
  const totals = campaigns.reduce(
    (a, c) => ({
      sent: a.sent + c.sentCount,
      delivered: a.delivered + c.deliveredCount,
      bounced: a.bounced + c.hardBounceCount,
      opened: a.opened + c.openedCount,
    }),
    { sent: 0, delivered: 0, bounced: 0, opened: 0 },
  );

  const openRate = totals.delivered > 0 ? ((totals.opened / totals.delivered) * 100).toFixed(1) : "—";
  const bounceRate = totals.sent > 0 ? ((totals.bounced / totals.sent) * 100).toFixed(2) : "—";

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Dashboard</h1>
      <p className="text-sm text-gray-500 mb-6">Your campaigns at a glance.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat label="Sent" value={totals.sent} />
        <Stat label="Delivered" value={totals.delivered} />
        <Stat label="Open rate" value={`${openRate}%`} />
        <Stat label="Bounce rate" value={`${bounceRate}%`} />
      </div>

      <h2 className="text-lg font-semibold text-gray-900 mb-3">Your campaigns</h2>
      {isLoading ? <p className="text-sm text-gray-500">Loading…</p> : null}
      {!isLoading && campaigns.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <p className="text-sm text-gray-500 mb-4">No campaigns yet.</p>
          <Link to="/campaigns" className="text-sm font-medium text-indigo-600 hover:underline">Create your first campaign →</Link>
        </div>
      ) : null}
      {campaigns.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Campaign</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Recipients</th>
                <th className="text-right px-4 py-2">Sent / Delivered</th>
                <th className="text-right px-4 py-2" title="Recipient mail clients that loaded the tracking pixel. Apple Mail / Gmail can pre-fetch this — treat as a floor.">Opens</th>
                <th className="text-right px-4 py-2" title="Engaged - unsubscribe clicks. Link tracking counts the unsub footer as a click too.">Clicks</th>
                <th className="text-right px-4 py-2">Bounces</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/campaigns/${c.id}`} className="font-medium text-indigo-600 hover:underline">{c.name}</Link>
                    <div className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleString()}</div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge s={c.status} /></td>
                  <td className="px-4 py-3 text-right">{c.totalRecipients}</td>
                  <td className="px-4 py-3 text-right">{c.sentCount} / {c.deliveredCount}</td>
                  <td className="px-4 py-3 text-right">{c.openedCount}</td>
                  <td className="px-4 py-3 text-right"><ClickSplit clicked={c.clickedCount} unsubscribed={c.unsubscribedCount} compact /></td>
                  <td className="px-4 py-3 text-right">{c.hardBounceCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ s }: { s: string }) {
  const map: Record<string, string> = {
    draft: "bg-gray-100 text-gray-700",
    scheduled: "bg-blue-100 text-blue-700",
    sending: "bg-amber-100 text-amber-700",
    paused: "bg-orange-100 text-orange-700",
    done: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
    cancelled: "bg-gray-100 text-gray-500",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${map[s] ?? "bg-gray-100 text-gray-700"}`}>{s}</span>;
}
